import * as utils from '../utils';

export type RSVPParam = {
  id?: number,
  eventId: number,
  status?: 'attending' | 'not_attending',
  name?: string,
  contact?: string,
  numberOfTotalAttendees?: number,
  numberOfAdultAttendees?: number,
  numberOfChildrenAttendees?: number,
  note?: string,
};

export type RSVP = {
  id: number,
  eventId: number,
  status: 'attending' | 'not_attending',
  name: string,
  contact: string,
  numberOfAdultAttendees?: number,
  numberOfChildrenAttendees?: number,
  note: string,
  createdAt: string,
};

export const updateData = async (mobigramUuid: string, data: RSVPParam): Promise<utils.IProviderResponse<RSVP>> => {
  const body: Record<string, string | number> = {
    mobigram_uuid: mobigramUuid,
    event_id: data.eventId,
  };

  if (data?.id) {
    body.rsvp_id = data.id;
  }
  if (data?.status) {
    body.status = data.status;
  }
  if (data?.name) {
    body.attendee_name = data.name;
  }
  if (data?.contact) {
    body.attendee_contact_info = data.contact;
  }
  if (typeof data?.numberOfTotalAttendees === 'number') {
    body.number_of_total_attendees = data.numberOfTotalAttendees;
  }
  if (typeof data?.numberOfAdultAttendees === 'number') {
    body.number_of_adult_attendees = data.numberOfAdultAttendees;
  }
  if (typeof data?.numberOfChildrenAttendees === 'number') {
    body.number_of_children_attendees = data.numberOfChildrenAttendees;
  }

  let response = {
    rsvp: null as Record<string, string | number> | null,
    error: null as string | null,
  };

  try {
    response = await fetch(`${process.env.REACT_APP_REST_API_ADDRESS}/api/v1/rsvps`, {
      method: 'post',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(body),
    }).then((res) => res.json());
  } catch (error) {
    response.error = (error as Error).message;
  }

  if (response?.error || !response.rsvp) {
    return utils.ResponseError(response?.error || 'unknown error');
  }

  return utils.ResponseData<RSVP>({
    id: Number(response.rsvp.id),
    eventId: Number(response.rsvp.event_id),
    name: String(response.rsvp.attendee_name),
    contact: String(response.rsvp.attendee_contact_info),
    numberOfAdultAttendees: Number(response.rsvp.number_of_adult_attendees),
    numberOfChildrenAttendees: Number(response.rsvp.number_of_children_attendees),
    note: String(response.rsvp.reply_message),
    status: response.rsvp.status as 'attending' | 'not_attending',
    createdAt: String(response.rsvp.created_at),
  });
};

export const updateReply = async (mobigramUuid: string, rsvp: RSVPParam, note: string): Promise<utils.IProviderResponse<RSVP>> => {
  const body = {
    mobigram_uuid: mobigramUuid,
    event_id: rsvp.eventId,
    rsvp_id: rsvp.id,
    reply_message: note || rsvp.note || '',
  };

  let response = {
    rsvp: null as Record<string, string | number> | null,
    error: null as string | null,
  };

  try {
    response = await fetch(`${process.env.REACT_APP_REST_API_ADDRESS}/api/v1/rsvps/reply_message`, {
      method: 'post',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(body),
    }).then((res) => res.json());
  } catch (error) {
    response.error = (error as Error).message;
  }

  if (response?.error || !response.rsvp) {
    return utils.ResponseError(response?.error || 'unknown error');
  }

  return utils.ResponseData<RSVP>({
    id: Number(response.rsvp.id),
    eventId: Number(response.rsvp.event_id),
    name: String(response.rsvp.attendee_name),
    contact: String(response.rsvp.attendee_contact_info),
    numberOfAdultAttendees: Number(response.rsvp.number_of_adult_attendees),
    numberOfChildrenAttendees: Number(response.rsvp.number_of_children_attendees),
    note: String(response.rsvp.reply_message),
    status: response.rsvp.status as 'attending' | 'not_attending',
    createdAt: String(response.rsvp.created_at),
  });
};

export const update = async (mobigramUuid: string, data: RSVPParam): Promise<utils.IProviderResponse<RSVP>> => {
  let updatedRSVP: RSVP | null = null;
  if ((data.numberOfTotalAttendees || 0) + (data.numberOfAdultAttendees || 0) + (data.numberOfChildrenAttendees || 0) > 0) {
    const updateDataResult = await updateData(mobigramUuid, data);

    if (!updateDataResult.data || updateDataResult.error) {
      return updateDataResult;
    }

    updatedRSVP = updateDataResult.data;
  }

  if (data.note) {
    const updateReplyResult = await updateReply(mobigramUuid, data, data.note);

    if (!updateReplyResult.data || updateReplyResult.error) {
      return utils.ResponseError(updateReplyResult.error || 'unknown error');
    }

    updatedRSVP = updateReplyResult.data;
  }

  if (!updatedRSVP) {
    return utils.ResponseError('unknown error');
  }

  return utils.ResponseData<RSVP>(updatedRSVP);
};
